import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, User } from 'lucide-react';

export default function EmployeeLogin() {
  // Pre-fill the email from a ?email= link (e.g. an onboarding/login link) so the
  // user only types their password. Stays editable if they need to change it.
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // One login against the CRM backend. This used to hit two servers — an
      // external one for identity, then the CRM for lead access — which broke in
      // production, where only the CRM exists.
      const response = await fetch('/crm-api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        // crm_* keys drive the lead store for both roles.
        localStorage.setItem('crm_token', data.token);
        localStorage.setItem('crm_user', JSON.stringify(data.user));

        // An admin who arrives on the employee login link still belongs in the
        // admin area — route by role instead of always landing on employee home.
        if (data.user.role === 'admin') {
          localStorage.setItem('token', data.token);
          navigate('/dashboard');
        } else {
          // employee_* keys drive the employee area.
          localStorage.setItem('employee_token', data.token);
          localStorage.setItem('employee_id', data.user.id);
          localStorage.setItem('employee_name', data.user.name);
          localStorage.setItem('employee_slug', data.user.profileSlug);
          navigate('/employee/home');
        }
      } else {
        setError(data.error || data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#141517] via-[#2b2d31] to-[#0f1011] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-white rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl p-2">
            <img src="/tecgs-logo.png" alt="TECGSOFT" className="max-w-full max-h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">
            TECGSOFT
          </h1>
          <p className="text-gray-300 font-medium">Employee Portal</p>
          <p className="text-gray-400/80 text-sm mt-1">Sign in to access your dashboard</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="your.email@company.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <a href="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#c9a24b] to-[#a5822c] text-white py-3 rounded-lg font-semibold hover:from-[#b8912f] hover:to-[#8a6a20] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Demo credentials: Use your employee email
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-300 hover:text-white text-sm transition-colors"
          >
            ← Back to Home
          </button>
        </div>

        <div className="text-center mt-6">
          <p className="text-gray-300 text-sm">
            © 2026 TECGSOFT
          </p>
        </div>
      </div>
    </div>
  );
}
